import type { FC } from 'react';
import { CalendarCheck, CheckCircle2, Mail, Truck, Wallet } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, optStr, str } from '@/app/lib/a2ui/props.ts';

const messages = defineMessages({
  confirmed: {
    id: 'orderConfirmation.confirmed',
    defaultMessage: 'Order placed',
    description: 'Status line confirming the input order has been written to the clinic records.',
  },
  orderId: {
    id: 'orderConfirmation.orderId',
    defaultMessage: 'Order ID',
    description: 'Label before the clinic order identifier.',
  },
  delivery: {
    id: 'orderConfirmation.delivery',
    defaultMessage: 'Delivery',
    description: 'Label before the confirmed delivery slot.',
  },
  payment: {
    id: 'orderConfirmation.payment',
    defaultMessage: 'Payment',
    description: 'Label before how the farmer pays for the order.',
  },
  fieldVisit: {
    id: 'orderConfirmation.fieldVisit',
    defaultMessage: 'Field visit',
    description: 'Label before the confirmed agronomist field-visit slot.',
  },
  payLink: {
    id: 'orderConfirmation.payLink',
    defaultMessage: 'Open the payment link',
    description: 'Link text opening the payment page for this order.',
  },
  followUp: {
    id: 'orderConfirmation.followUp',
    defaultMessage: 'Follow-up on {date}',
    description: 'Heading of the follow-up block, naming the date the clinic will check back.',
  },
  emailedTo: {
    id: 'orderConfirmation.emailedTo',
    defaultMessage: 'Plan emailed to {address}',
    description: 'Confirmation that the treatment plan was emailed to the farmer.',
  },
});

function readItems(value: unknown): string[] {
  return arr(value).flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const line = str((entry as Record<string, unknown>).line);
    return line ? [line] : [];
  });
}

export const OrderConfirmation: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const orderId = str(node.props.orderId);
  const total = str(node.props.total);
  const items = readItems(node.props.items);
  const deliverySlot = str(node.props.deliverySlot);
  const paymentMethod = str(node.props.paymentMethod);
  const paymentNote = optStr(node.props.paymentNote);
  const paymentLinkUrl = optStr(node.props.paymentLinkUrl);
  const followUpDate = str(node.props.followUpDate);
  const watchFor = str(node.props.watchFor);
  const emailedTo = optStr(node.props.emailedTo);
  const fieldVisit = optStr(node.props.fieldVisit);

  return (
    <div className="w-full">
      <div className="rounded-lg border-2 border-success/50 bg-card p-5">
        <p className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-caps text-success">
          <CheckCircle2 className="size-5 shrink-0" aria-hidden />
          {intl.formatMessage(messages.confirmed)}
        </p>

        <p className="mb-1 text-xs font-semibold uppercase tracking-caps text-muted-foreground">
          {intl.formatMessage(messages.orderId)}
        </p>
        <p
          className="mb-4 text-2xl font-bold tabular-nums tracking-tight text-foreground"
          translate="no"
        >
          {orderId}
        </p>

        <ul className="mb-4 space-y-1.5 border-t border-border pt-3">
          {items.map((line) => (
            <li key={line} className="text-base text-foreground" translate="no">
              {line}
            </li>
          ))}
        </ul>

        <dl className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-md bg-muted p-3.5">
            <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-caps text-muted-foreground">
              <Truck className="size-3.5 shrink-0" aria-hidden />
              {intl.formatMessage(messages.delivery)}
            </dt>
            <dd className="mt-0.5 text-base font-bold leading-snug text-foreground">
              {deliverySlot}
            </dd>
          </div>
          <div className="rounded-md bg-muted p-3.5">
            <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-caps text-muted-foreground">
              <Wallet className="size-3.5 shrink-0" aria-hidden />
              {intl.formatMessage(messages.payment)}
            </dt>
            <dd className="mt-0.5 text-base font-bold leading-snug text-foreground">
              {paymentMethod} — <span className="tabular-nums">{total}</span>
            </dd>
          </div>
          {fieldVisit ? (
            <div className="rounded-md bg-muted p-3.5 sm:col-span-2">
              <dt className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-caps text-muted-foreground">
                <CalendarCheck className="size-3.5 shrink-0" aria-hidden />
                {intl.formatMessage(messages.fieldVisit)}
              </dt>
              <dd className="mt-0.5 text-base font-bold leading-snug text-foreground">
                {fieldVisit}
              </dd>
            </div>
          ) : null}
        </dl>

        {paymentNote ? (
          <p className="mt-3 text-sm leading-snug text-muted-foreground">{paymentNote}</p>
        ) : null}
        {paymentLinkUrl ? (
          <a
            href={paymentLinkUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-block rounded-md bg-primary px-4 py-3 text-base font-bold text-primary-foreground transition-transform duration-150 ease-out hover:shadow-lift focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary active:scale-95"
          >
            {intl.formatMessage(messages.payLink)}
          </a>
        ) : null}
      </div>

      <div className="mt-4 rounded-lg border border-border bg-card p-5">
        <p className="mb-1.5 flex items-center gap-2 text-sm font-bold uppercase tracking-caps text-primary">
          <CalendarCheck className="size-4 shrink-0" aria-hidden />
          {intl.formatMessage(messages.followUp, { date: followUpDate })}
        </p>
        <p className="text-base leading-relaxed text-foreground">{watchFor}</p>
      </div>

      {emailedTo ? (
        <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Mail className="size-4 shrink-0" aria-hidden />
          <span className="min-w-0 truncate" translate="no">
            {intl.formatMessage(messages.emailedTo, { address: emailedTo })}
          </span>
        </p>
      ) : null}
    </div>
  );
};
